import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./Select";

// Radix Select forbids an empty-string item value, so the optional "(none)"
// choice uses this sentinel internally and is mapped back to `undefined`.
const NONE = "__none__";

export interface FieldSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: string[];
  /** When set, prepends a "(none)" choice that maps to `undefined`. */
  noneLabel?: string;
  placeholder?: string;
}

/**
 * A `Select` driven by a flat `options` list instead of hand-written items.
 * Folds in the empty-value sentinel so callers never handle it. For grouped or
 * rich-content menus, compose the lower-level `Select` parts directly.
 */
export function FieldSelect({
  value,
  onChange,
  options,
  noneLabel,
  placeholder,
}: FieldSelectProps) {
  // With a noneLabel, an unset value selects the "(none)" item (NONE sentinel).
  // Without one, leave the value undefined so the placeholder shows.
  const current = value ?? (noneLabel !== undefined ? NONE : undefined);

  return (
    <Select value={current} onValueChange={(v) => onChange(v === NONE ? undefined : v)}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {noneLabel !== undefined && <SelectItem value={NONE}>{noneLabel}</SelectItem>}
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {opt}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
